import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

// Finished Goods Inventory DTOs - matching backend DTOs
export interface FinishedGoodsInventorySummaryResponse {
  totalQuantity: number;
  totalValue: number;
  activeEndgoods: number;
  lowStockCount: number;
  overstockCount: number;
}

export interface FinishedGoodsInventorySummary {
  endgoodId: number;
  endgoodName: string;
  sku?: string;
  categoryName?: string;
  availableQuantity: number;
  totalValue: number;
  unitCost?: number;
  lastPackedDate?: string;
  lastInvoicedDate?: string;
  daysSinceLastActivity?: number;
}

export interface FinishedGoodsInventoryByStatus {
  endgoodId: number;
  endgoodName: string;
  sku?: string;
  categoryName?: string;
  availableQuantity: number;
  totalValue: number;
  unitCost?: number;
  status: 'PACKED' | 'INVOICED' | 'RESERVED';
  lastPackedDate?: string;
  lastInvoicedDate?: string;
}

export interface TopEndgood {
  endgoodId: number;
  endgoodName: string;
  sku?: string;
  categoryName?: string;
  availableQuantity?: number;
  quantity?: number;
  totalValue: number;
  totalBoxes?: number;
  unitCost?: number;
}

export interface LowStockAlert {
  endgoodId: number;
  endgoodName: string;
  sku?: string;
  currentStock: number;
  minimumStock?: number;
  suggestedReorderQuantity?: number;
}

export interface FinishedGoodsCategorySummary {
  categoryId?: number;
  categoryName?: string;
  quantity?: number;
  totalValue?: number;
  value?: number;
}

export interface FinishedGoodsInventoryViewItem {
  endgoodName: string;
  categoryName?: string;
  availableQuantity: number;
  totalBoxes?: number;
}

export interface FinishedGoodsInventoryExtendedSummary {
  endgoodId?: number;
  endgoodName?: string;
  sku?: string;
  branchId?: number;
  categoryId?: number;
  categoryName?: string;
  wipQuantity?: number;
  wipValue?: number;
  readyQuantity?: number;
  readyToPackQuantity?: number;
  readyToPackValue?: number;
  packedQuantity?: number;
  packedValue?: number;
  packedBoxes?: number;
  lastPackingDate?: string;
  lastInvoicingDate?: string;
  daysSinceLastActivity?: number;
  totalQuantity?: number;
  totalValue?: number;
  totalBoxes?: number;
}

export interface FinishedGoodsInventoryAgingSummary {
  bucketLabel?: string;
  ageBucket?: string;
  bucket?: string;
  label?: string;
  agingBucket?: string;
  inventoryType?: string;
  branchId?: number;
  categoryId?: number;
  categoryName?: string | null;
  quantity?: number;
  totalQuantity?: number;
  value?: number;
  totalValue?: number;
}

export interface FinishedGoodsInventoryAgingDetail {
  endgoodId?: number;
  endgoodName?: string;
  categoryId?: number;
  categoryName?: string | null;
  inventoryType?: string;
  bucketLabel?: string;
  ageBucket?: string;
  bucket?: string;
  label?: string;
  agingBucket?: string;
  quantity?: number;
  totalQuantity?: number;
  totalValue?: number;
  workOrderId?: number;
  workOrderNumber?: string;
  /** Numeric id or batch code (e.g. BH-SGE-20261017-0004) */
  batchId?: number | string;
  batchNumber?: string;
}

// Finished Goods Inventory API Service
class FinishedGoodsInventoryApiService extends BaseApiService {
  
  // Get inventory summary
  async getInventorySummary(branchId?: number): Promise<ApiResponse<FinishedGoodsInventorySummaryResponse>> {
    try {
      const response = await this.makeRequest<ApiResponse<FinishedGoodsInventorySummaryResponse>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.SUMMARY(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      // Backend already returns ApiResponse format, so return it directly
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching inventory summary:', error);
      toast.error('Failed to fetch inventory summary');
      throw error;
    }
  }

  // Get all inventory
  async getAllInventory(branchId?: number): Promise<ApiResponse<FinishedGoodsInventorySummary[]>> {
    try {
      const data = await this.makeRequest<FinishedGoodsInventorySummary[]>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.ALL(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      return {
        success: true,
        message: 'All inventory fetched successfully',
        data: data
      };
    } catch (error) {
      // console.error('FinishedGoodsInventoryApiService: Error fetching all inventory:', error);
      toast.error('Failed to fetch all inventory');
      throw error;
    }
  }

  // Get inventory by status
  async getInventoryByStatus(branchId?: number, status: 'PACKED' | 'INVOICED' | 'RESERVED' = 'PACKED'): Promise<ApiResponse<FinishedGoodsInventoryByStatus[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<FinishedGoodsInventoryByStatus[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.BY_STATUS(branchId, status),
        { method: HTTP_METHODS.GET }
      );
      
      // Backend already returns ApiResponse format, so return it directly
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching inventory by status:', error);
      toast.error('Failed to fetch inventory by status');
      throw error;
    }
  }

  // Get top endgoods by value
  async getTopEndgoodsByValue(branchId?: number, limit: number = 10): Promise<ApiResponse<TopEndgood[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<TopEndgood[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.TOP_BY_VALUE(branchId, limit),
        { method: HTTP_METHODS.GET }
      );
      
      // Backend already returns ApiResponse format, so return it directly
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching top endgoods by value:', error);
      toast.error('Failed to fetch top endgoods by value');
      throw error;
    }
  }

  // Get top endgoods by quantity
  async getTopEndgoodsByQuantity(branchId?: number, limit: number = 10): Promise<ApiResponse<TopEndgood[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<TopEndgood[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.TOP_BY_QUANTITY(branchId, limit),
        { method: HTTP_METHODS.GET }
      );
      
      // Backend already returns ApiResponse format, so return it directly
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching top endgoods by quantity:', error);
      toast.error('Failed to fetch top endgoods by quantity');
      throw error;
    }
  }

  // Get low stock alerts
  async getLowStockAlerts(branchId?: number, threshold?: number): Promise<ApiResponse<LowStockAlert[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<LowStockAlert[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.LOW_STOCK_ALERTS(branchId, threshold),
        { method: HTTP_METHODS.GET }
      );
      
      // Backend already returns ApiResponse format, so return it directly
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching low stock alerts:', error);
      toast.error('Failed to fetch low stock alerts');
      throw error;
    }
  }

  // Get inventory by category
  async getInventoryByCategory(branchId?: number): Promise<ApiResponse<FinishedGoodsCategorySummary[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<FinishedGoodsCategorySummary[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.BY_CATEGORY(branchId),
        { method: HTTP_METHODS.GET }
      );

      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching inventory by category:', error);
      toast.error('Failed to fetch category breakdown');
      throw error;
    }
  }

  // Get finished goods inventory view
  async getFinishedGoodsInventoryView(): Promise<ApiResponse<FinishedGoodsInventoryViewItem[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<FinishedGoodsInventoryViewItem[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.VIEW,
        { method: HTTP_METHODS.GET }
      );

      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching finished goods view:', error);
      toast.error('Failed to fetch finished goods inventory');
      throw error;
    }
  }

  // Get finished goods inventory extended view
  async getFinishedGoodsInventoryExtendedView(
    branchId?: number,
    categoryId?: number,
    endgoodId?: number
  ): Promise<ApiResponse<FinishedGoodsInventoryExtendedSummary[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<FinishedGoodsInventoryExtendedSummary[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.VIEW_EXTENDED(branchId, categoryId, endgoodId),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching extended view:', error);
      toast.error('Failed to fetch finished goods inventory extended view');
      throw error;
    }
  }

  async getInventoryAgingSummary(
    branchId?: number,
    categoryId?: number,
    inventoryType?: string
  ): Promise<ApiResponse<FinishedGoodsInventoryAgingSummary[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<FinishedGoodsInventoryAgingSummary[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.AGING_SUMMARY(branchId, categoryId, inventoryType),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching aging summary:', error);
      toast.error('Failed to fetch aging summary');
      throw error;
    }
  }

  async getInventoryAgingDetails(
    branchId?: number,
    categoryId?: number,
    inventoryType?: string,
    agingBucket?: string,
    endgoodId?: number
  ): Promise<ApiResponse<FinishedGoodsInventoryAgingDetail[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<FinishedGoodsInventoryAgingDetail[]>>(
        API_ENDPOINTS.FINISHED_GOODS_DASHBOARD.AGING_DETAILS(
          branchId,
          categoryId,
          inventoryType,
          agingBucket,
          endgoodId
        ),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      console.error('FinishedGoodsInventoryApiService: Error fetching aging details:', error);
      toast.error('Failed to fetch aging details');
      throw error;
    }
  }
}

export const finishedGoodsInventoryApiService = new FinishedGoodsInventoryApiService();

